import type { Membership } from 'api-client';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { Key } from 'react-aria-components';
import { useListData } from 'react-stately';

import { MultiSelect } from '@/lib/base/forms/select/multi-select';
import type { SelectItemType } from '@/lib/base/forms/select/select';

export interface TeamMembersInputProps {
  value: unknown;
  onChange: (next: unknown) => void;
  disabled: boolean;
  members: ReadonlyArray<Membership>;
}

const renderItem = (item: SelectItemType) => (
  <MultiSelect.Item
    id={item.id}
    label={item.label}
    supportingText={item.supportingText}
  />
);

export const TeamMembersInput: React.FC<TeamMembersInputProps> = ({
  value,
  onChange,
  disabled,
  members,
}) => {
  const valueArray = useMemo<string[]>(
    () =>
      Array.isArray(value) && value.every(entry => typeof entry === 'string')
        ? (value as string[])
        : [],
    [value]
  );

  const items = useMemo<SelectItemType[]>(
    () =>
      members.map(member => ({
        id: member.entityId,
        label:
          member.displayName ||
          `${member.firstName} ${member.lastName}`.trim() ||
          member.email,
        supportingText: member.email,
      })),
    [members]
  );

  const initialSelectedItems = useMemo<SelectItemType[]>(
    () =>
      valueArray
        .map(id => items.find(item => item.id === id))
        .filter((item): item is SelectItemType => item !== undefined),
    [items, valueArray]
  );

  const selectedItems = useListData<SelectItemType>({
    initialItems: initialSelectedItems,
  });

  useEffect(() => {
    const currentIds = selectedItems.items.map(i => String(i.id));
    const desiredIds = valueArray;
    if (JSON.stringify(currentIds) === JSON.stringify(desiredIds)) {
      return;
    }
    const desiredItems = desiredIds
      .map(id => items.find(item => item.id === id))
      .filter((item): item is SelectItemType => item !== undefined);
    if (currentIds.length > 0) {
      selectedItems.remove(...currentIds);
    }
    if (desiredItems.length > 0) {
      selectedItems.append(...desiredItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedItems from useListData is a new object each render; reading it via closure is safe because deps change whenever the value we need to sync changes
  }, [valueArray, items]);

  const handleItemInserted = useCallback(
    (key: Key) => {
      onChange([...valueArray, String(key)]);
    },
    [valueArray, onChange]
  );

  const handleItemCleared = useCallback(
    (key: Key) => {
      onChange(valueArray.filter(v => v !== String(key)));
    },
    [valueArray, onChange]
  );

  return (
    <MultiSelect
      placeholder='Search team members'
      shortcut={false}
      items={items}
      selectedItems={selectedItems}
      onItemInserted={handleItemInserted}
      onItemCleared={handleItemCleared}
      isDisabled={disabled}
      aria-label='Team members'
      size='sm'
    >
      {renderItem}
    </MultiSelect>
  );
};
